import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

const TermsOfUse = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Terms of Use - Solis Games</title>
        <meta name="description" content="Terms of Use for Solis Games - Learn about the rules and guidelines for using our gaming platform." />
      </Helmet>
      
      <Header />
      
      <main className="pt-16 pb-20 md:pb-8">
        <div className="container mx-auto px-4 lg:px-8 py-8 max-w-4xl">
          <div className="bg-card rounded-lg border border-border p-8">
            <h1 className="text-4xl font-bold text-foreground mb-6">Terms of Use</h1>
            
            <p className="text-muted-foreground mb-8">
              These terms apply to all games and services on the Solis Games platform.
            </p>
            
            <div className="text-sm text-muted-foreground mb-8">
              <strong>This policy was last updated:</strong> February 26, 2025
            </div>

            <div className="prose prose-invert max-w-none">
              <p className="text-foreground mb-6">
                These are the terms of use (including EULA terms) by Solis Interactive Inc. ("we," "us," or "Solis Interactive"). 
                These Terms of Use ("Terms") govern your access to and use of the Solis Games platform, including our website 
                and all games delivered via WebGPU technology (collectively, the "Service"). By accessing or using the Service, 
                you agree to these Terms. If you don't agree, please don't use the Service.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">1. Eligibility</h3>
              <p className="text-foreground mb-6">
                You must be at least 13 years old (or 16 in the EU/EEA) to use the Service. By using it, you confirm you 
                meet this age requirement and can legally agree to these Terms—or that your parent/guardian consents on your behalf.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">2. License to Use the Service</h3>
              <p className="text-foreground mb-4">
                We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Solis Games 
                platform for personal, non-commercial use. Games are delivered via WebGPU technology and run in your browser. 
                You don't own the games—you're licensed to use them under these Terms. You may not:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>Copy, modify, or reverse-engineer any games or the Service.</li>
                <li>Distribute, sell, or sublicense any part of the Service.</li>
                <li>Use cheats, hacks, or unauthorized tools to alter gameplay.</li>
                <li>Attempt to bypass any security measures or access restrictions.</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">3. Account and Access</h3>
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>
                  <strong>Account Creation:</strong> You may need an account to access certain features. Keep your login 
                  details secure—you're responsible for all activity under your account.
                </li>
                <li>
                  <strong>Browser Requirements:</strong> The Service uses WebGPU and runs in supported browsers. You're 
                  responsible for maintaining compatible hardware and software.
                </li>
                <li>
                  <strong>Service Availability:</strong> We aim to keep the Service online but can't guarantee it. 
                  Downtime (e.g., server maintenance, technical issues) may happen without notice.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">4. User Conduct</h3>
              <p className="text-foreground mb-4">
                You agree to use the Service lawfully and respectfully. Don't:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>Harass, threaten, or abuse other players (e.g., via chat or voice).</li>
                <li>Post or share offensive, illegal, or infringing content.</li>
                <li>Exploit bugs or glitches for unfair advantage—report them to infosec@solisinteractive.com.</li>
                <li>Impersonate others or misrepresent your affiliation with us.</li>
                <li>Use the Service for any commercial purposes without our written consent.</li>
                <li>Attempt to disrupt or interfere with the Service or other users' experience.</li>
              </ul>
              
              <p className="text-foreground mb-6">
                We may ban or suspend your account for violations, at our discretion, without warning.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">5. Intellectual Property</h3>
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>
                  <strong>Our Content:</strong> The Service, including all games, code, art, music, and design, is owned 
                  by Solis Interactive or our licensors. It's protected by copyright, trademark, and other laws.
                </li>
                <li>
                  <strong>Your Content:</strong> If you create content within games or purchase virtual items, you keep 
                  ownership but grant us a worldwide, perpetual, royalty-free license to use, display, and distribute it 
                  within the Service.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">6. Payments, Virtual Items, and Refund Policy</h3>
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>
                  <strong>Subscriptions:</strong> Access to premium games requires an active subscription. Payments are 
                  processed through third-party providers.
                </li>
                <li>
                  <strong>Virtual Items:</strong> You get a license to use virtual items, not ownership. They're 
                  non-refundable and may become unavailable if we discontinue the Service.
                </li>
                <li>
                  <strong>Marketplace:</strong> If we launch an in-game marketplace, you can trade or purchase content 
                  from other users. These transactions are facilitated by us, but we offer no refunds for transactions 
                  both parties agreed upon.
                </li>
                <li>
                  <strong>Refund Policy:</strong> All sales are final unless law requires otherwise (e.g., EU's 14-day refund right).
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">7. Platform Features</h3>
              <p className="text-foreground mb-6">
                The Service may include chat, voice, leaderboards, and social features. Your username and actions 
                (e.g., stats) may be visible to others. Follow our Community Guidelines. We may monitor or record 
                interactions to enforce these Terms.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">8. Termination</h3>
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>
                  <strong>By You:</strong> Stop using the Service anytime—delete your account via privacy@solisinteractive.com.
                </li>
                <li>
                  <strong>By Us:</strong> We can suspend or terminate your access for violating these Terms, or if we 
                  discontinue the Service, with or without notice. No refunds unless required by law.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">9. Disclaimers</h3>
              <p className="text-foreground mb-6">
                The Service is provided "as is." We don't guarantee it will be uninterrupted, bug-free, or secure. 
                WebGPU's experimental nature or browser updates might affect performance. We're not liable for 
                third-party issues or technical problems beyond our control.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">10. Limitation of Liability and Arbitration Agreement</h3>
              <p className="text-foreground mb-4">
                <strong>Limitation of Liability:</strong> To the fullest extent allowed by law, Solis Interactive isn't 
                liable for indirect, incidental, or consequential damages (e.g., lost data, hardware damage, computer 
                performance issues) from using the Service. Our total liability won't exceed what you paid us in the 
                last 12 months (if anything).
              </p>
              
              <p className="text-foreground mb-6">
                <strong>Agreement to Arbitrate:</strong> You and Solis Interactive agree that any dispute, claim, or 
                controversy arising out of or relating to these Terms or the Service will be resolved by binding arbitration, 
                not in court, except for small claims or injunctive relief. Arbitration will follow the American Arbitration 
                Association (AAA) rules, conducted in Chicago, IL USA or remotely if agreed. You waive your right to a jury 
                trial or class action. Contact us at legal@solisinteractive.com to start informal resolution first.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">11. Indemnity</h3>
              <p className="text-foreground mb-6">
                You'll defend and compensate us for any claims, losses, or damages arising from your misuse of the Service, 
                violation of these Terms, or infringement of others' rights.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">12. Governing Law and Disputes</h3>
              <p className="text-foreground mb-6">
                These Terms are governed by Illinois, USA law, excluding conflict-of-law rules. Disputes go to Cook County, 
                IL courts, unless you're in a region (e.g., EU) where local law mandates otherwise. We'll try informal 
                resolution first—email us at legal@solisinteractive.com.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">13. Changes to These Terms</h3>
              <p className="text-foreground mb-6">
                We may update these Terms as the Service evolves. Check back here. Continued use after changes means you 
                accept them. Big updates get notified via email or platform notification if required.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">14. Miscellaneous</h3>
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>If any part of these Terms is unenforceable, the rest still applies.</li>
                <li>No waiver of a term means we waive it forever.</li>
                <li>These Terms, plus the Privacy Policy and Community Guidelines, are the full agreement between us.</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">15. Contact Us</h3>
              <p className="text-foreground mb-4">Questions? Reach Solis Interactive Inc. at:</p>
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>Email: <a href="mailto:contact@solisinteractive.com" className="text-primary hover:underline">contact@solisinteractive.com</a></li>
                <li>Address: 1440 W Taylor St PMB 4217 Chicago, Illinois 60607, United States</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default TermsOfUse;
